import { useEffect, useMemo, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { Download, Filter, MapPin, LogIn, LogOut, Search, RefreshCw, ExternalLink, X } from "lucide-react";
import { DateInput } from "../components/DateInput";
import { SearchSelect } from "../components/SearchSelect";

const KIND_LABEL = {
  in: { label: "Prise de service", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: LogIn },
  out: { label: "Fin de service", cls: "bg-rose-100 text-rose-800 border-rose-300", Icon: LogOut },
};

function fmtDT(iso) {
  try {
    const d = new Date(iso);
    return { date: d.toLocaleDateString("fr-FR"), time: d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) };
  } catch { return { date: (iso || "").slice(0, 10), time: (iso || "").slice(11, 19) }; }
}

function todayISO() { return new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" }); }
function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function Pointages() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { lastEvent } = useRealtime();

  // Filtres
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(todayISO());
  const [siteId, setSiteId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [kind, setKind] = useState(""); // "", "in", "out"
  const [sortBy, setSortBy] = useState("date"); // "date" | "site"
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (start && end) { params.set("start", start); params.set("end", end); }
      if (siteId) params.set("site_id", siteId);
      if (agentId) params.set("agent_id", agentId);
      if (kind) params.set("kind", kind);
      const r = await http.get(`/check-ins?${params.toString()}`);
      setItems(r.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([http.get("/sites"), http.get("/agents")]);
        setSites(s.data || []);
        setAgents(a.data || []);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end, siteId, agentId, kind]);
  useEffect(() => {
    if (lastEvent?.type === "checkin_changed") load();
    // eslint-disable-next-line
  }, [lastEvent]);

  const downloadPdf = async () => {
    const params = new URLSearchParams();
    if (start && end) { params.set("start", start); params.set("end", end); }
    if (siteId) params.set("site_id", siteId);
    if (agentId) params.set("agent_id", agentId);
    if (kind) params.set("kind", kind);
    params.set("sort_by", sortBy);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/check-ins/export.pdf?${params.toString()}`, {
        credentials: "include",
      });
      if (!resp.ok) throw new Error("Erreur téléchargement");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Pointages_${start}_${end}${sortBy === "site" ? "_par-site" : ""}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(String(e.message || e)); }
  };

  const resetFilters = () => {
    setStart(daysAgoISO(30)); setEnd(todayISO());
    setSiteId(""); setAgentId(""); setKind(""); setQ("");
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(it => {
      const s = `${it.agent_name || ""} ${it.site_name || ""} ${it.note || ""}`.toLowerCase();
      return s.includes(term);
    });
  }, [items, q]);

  const stats = useMemo(() => {
    let nIn = 0, nOut = 0, nOff = 0;
    for (const it of filtered) {
      if (it.kind === "in") nIn++;
      if (it.kind === "out") nOut++;
      if (it.off_site) nOff++;
    }
    return { nIn, nOut, nOff, total: filtered.length };
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="pointages-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-extrabold">Pointages agents</h1>
          <p className="text-sm text-slate-600">Prises et fins de service enregistrées depuis l'application mobile agent, avec coordonnées GPS et distance au site.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} data-testid="btn-refresh"
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-60">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}/> Rafraîchir
          </button>
          <button onClick={downloadPdf} data-testid="btn-download-pdf"
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold text-white rounded shadow-sm"
                  style={{ backgroundColor: "var(--brand-primary)" }}>
            <Download className="h-4 w-4"/> Exporter PDF
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="flex items-center gap-2 mb-3 text-slate-600">
          <Filter className="h-4 w-4"/>
          <span className="text-[10px] font-bold uppercase tracking-widest">Filtres</span>
          <button onClick={resetFilters} className="ml-auto text-[11px] text-slate-500 hover:text-slate-900 underline">Réinitialiser</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Du</div>
            <DateInput data-testid="filter-start" value={start} onChange={(e)=>setStart(e.target.value)}
                   className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Au</div>
            <DateInput data-testid="filter-end" value={end} onChange={(e)=>setEnd(e.target.value)}
                   className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Site</div>
            <SearchSelect data-testid="filter-site" value={siteId} onChange={(e)=>setSiteId(e.target.value)}
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
              <option value="">Tous les sites</option>
              {sites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </SearchSelect>
          </label>
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Agent</div>
            <select data-testid="filter-agent" value={agentId} onChange={(e)=>setAgentId(e.target.value)}
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
              <option value="">Tous les agents</option>
              {agents.map(a=><option key={a.id} value={a.id}>{a.last_name} {a.first_name}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Type</div>
            <select data-testid="filter-kind" value={kind} onChange={(e)=>setKind(e.target.value)}
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
              <option value="">Prises + Fins</option>
              <option value="in">Prises uniquement</option>
              <option value="out">Fins uniquement</option>
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Tri PDF</div>
            <select data-testid="filter-sort" value={sortBy} onChange={(e)=>setSortBy(e.target.value)}
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
              <option value="date">Chronologique</option>
              <option value="site">Regroupé par site</option>
            </select>
          </label>
        </div>
        <div className="mt-3 relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400"/>
          <input value={q} onChange={(e)=>setQ(e.target.value)} data-testid="filter-search"
                 placeholder="Rechercher dans agent / site / note…"
                 className="pl-8 pr-3 py-2 text-sm border border-slate-300 rounded w-full"/>
          {q && <button onClick={()=>setQ("")} className="absolute right-2 top-2 p-1 rounded hover:bg-slate-100"><X className="h-3.5 w-3.5 text-slate-400"/></button>}
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-md p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Total pointages</div>
          <div className="font-display text-2xl font-extrabold" data-testid="kpi-total">{stats.total}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Prises de service</div>
          <div className="font-display text-2xl font-extrabold text-emerald-800" data-testid="kpi-in">{stats.nIn}</div>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-rose-700">Fins de service</div>
          <div className="font-display text-2xl font-extrabold text-rose-800" data-testid="kpi-out">{stats.nOut}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Hors zone (géofence)</div>
          <div className="font-display text-2xl font-extrabold text-amber-800" data-testid="kpi-offsite">{stats.nOff}</div>
        </div>
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>}

      {/* Tableau */}
      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="py-2 px-3 text-left">Date · Heure</th>
                <th className="py-2 px-3 text-left">Type</th>
                <th className="py-2 px-3 text-left">Agent</th>
                <th className="py-2 px-3 text-left">Site</th>
                <th className="py-2 px-3 text-left">Coordonnées GPS</th>
                <th className="py-2 px-3 text-left">Distance</th>
                <th className="py-2 px-3 text-left">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="py-10 text-center text-slate-500">
                  {loading ? "Chargement…" : "Aucun pointage sur la période sélectionnée."}
                </td></tr>
              ) : filtered.map(it => {
                const { date, time } = fmtDT(it.at);
                const K = KIND_LABEL[it.kind] || KIND_LABEL.in;
                const Ic = K.Icon;
                return (
                  <tr key={it.id} className="hover:bg-slate-50" data-testid={`row-${it.id}`}>
                    <td className="py-2 px-3">
                      <div className="font-medium">{date}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{time}</div>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-widest ${K.cls}`}>
                        <Ic className="h-3 w-3"/> {K.label}
                      </span>
                    </td>
                    <td className="py-2 px-3">{it.agent_name || "—"}</td>
                    <td className="py-2 px-3">{it.site_name || <span className="text-slate-400 italic">Sans site</span>}</td>
                    <td className="py-2 px-3">
                      {it.lat != null && it.lon != null ? (
                        <a href={`https://maps.google.com/?q=${it.lat},${it.lon}`} target="_blank" rel="noopener noreferrer"
                           className="text-sky-700 hover:underline inline-flex items-center gap-1 text-xs font-mono">
                          <MapPin className="h-3 w-3"/> {it.lat.toFixed(6)}, {it.lon.toFixed(6)}
                          <ExternalLink className="h-3 w-3 opacity-60"/>
                        </a>
                      ) : <span className="text-slate-400">—</span>}
                      {it.accuracy != null && <div className="text-[10px] text-slate-400">± {Math.round(it.accuracy)} m</div>}
                    </td>
                    <td className="py-2 px-3">
                      {it.distance_m != null ? (
                        <span className={`text-xs font-mono font-semibold ${it.off_site ? "text-rose-600" : "text-emerald-600"}`}>
                          {Math.round(it.distance_m)} m
                          {it.off_site && <span className="ml-1 text-[10px] font-bold uppercase tracking-widest bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">Hors zone</span>}
                        </span>
                      ) : <span className="text-slate-400">—</span>}
                      {it.radius_m != null && <div className="text-[10px] text-slate-400">zone : {it.radius_m} m</div>}
                    </td>
                    <td className="py-2 px-3 text-xs text-slate-600 max-w-xs truncate" title={it.note}>{it.note || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
